<template>
    <div class="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div class="sm:mx-auto sm:w-full sm:max-w-md">
            <div class="text-center">
                <div class="mb-6 flex justify-center">
                    <jet-application-mark class="h-9 w-auto" />
                </div>
                <h2 class="text-3xl font-bold text-gray-900">Team Invitation</h2>
                <p class="mt-2 text-gray-600">Join {{ organizationName }}</p>
            </div>
        </div>

        <div class="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                <div class="mb-6 text-center">
                    <p class="text-gray-700">
                        <strong>{{ inviterName }}</strong> has invited you to join 
                        <strong>{{ organizationName }}</strong> as {{ roleName.toLowerCase() === 'admin' ? 'an' : 'a' }} 
                        <strong>{{ roleName }}</strong>.
                    </p>
                </div>

                <div v-if="existingUser" class="mb-6">
                    <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p class="text-sm text-blue-800">
                            <font-awesome-icon :icon="['fas', 'info-circle']" class="mr-2" />
                            We found an existing account with this email. You'll be added to the team automatically.
                        </p>
                    </div>
                </div>

                <form @submit.prevent="handleAccept" v-if="!existingUser">
                    <div class="space-y-6">
                        <div>
                            <label for="name" class="block text-sm font-medium text-gray-700">
                                Full Name
                            </label>
                            <input
                                id="name"
                                name="name"
                                type="text"
                                v-model="form.name"
                                required
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-orange-500 focus:ring-orange-500"
                                :class="{ 'border-red-500': form.errors.name }"
                            />
                            <div v-if="form.errors.name" class="text-red-600 text-sm mt-1">
                                {{ form.errors.name }}
                            </div>
                        </div>

                        <div>
                            <label for="email" class="block text-sm font-medium text-gray-700">
                                Email Address
                            </label>
                            <input
                                id="email"
                                type="email"
                                :value="invitation.email"
                                disabled
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-500"
                            />
                        </div>

                        <div>
                            <label for="password" class="block text-sm font-medium text-gray-700">
                                Password
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                v-model="form.password"
                                required
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-orange-500 focus:ring-orange-500"
                                :class="{ 'border-red-500': form.errors.password }"
                            />
                            <div v-if="form.errors.password" class="text-red-600 text-sm mt-1">
                                {{ form.errors.password }}
                            </div>
                        </div>

                        <div>
                            <label for="password_confirmation" class="block text-sm font-medium text-gray-700">
                                Confirm Password
                            </label>
                            <input
                                id="password_confirmation"
                                name="password_confirmation"
                                type="password"
                                v-model="form.password_confirmation"
                                required
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-orange-500 focus:ring-orange-500"
                            />
                        </div>
                    </div>
                </form>

                <div class="mt-6">
                    <button
                        @click="handleAccept"
                        :disabled="form.processing"
                        class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                    >
                        <font-awesome-icon v-if="form.processing" :icon="['fas', 'spinner']" class="fa-spin mr-2" />
                        {{ existingUser ? 'Join Team' : 'Create Account & Join Team' }}
                    </button>
                </div>

                <div class="mt-4 text-center">
                    <p class="text-xs text-gray-500">
                        This invitation expires on {{ new Date(invitation.expires_at).toLocaleDateString() }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { useForm } from '@inertiajs/vue3';

const props = defineProps({
    invitation: Object,
    existingUser: Object,
    organizationName: String,
    inviterName: String,
    roleName: String,
});

const form = useForm({
    name: '',
    password: '',
    password_confirmation: '',
});

const handleAccept = () => {
    form.post(route('team.invitation.submit', props.invitation.invitation_token), {
        preserveState: true,
        preserveScroll: true,
    });
};
</script>